import React, { useState, useRef } from 'react';
import FormTextField from 'components/common/FormTextField';
import FormField from 'components/common/FormField';
import MethodOfContact from 'components/common/MethodOfContact';
import MethodOfContactTypes from 'models/types/MethodOfContactTypes';
import { AlertCircle } from 'lucide-react';

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    notes: string;
    birthDate: string;
    preferredMethodOfContact: MethodOfContactTypes;
}

interface FormErrors {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
}

/**
 * Demo page to showcase the reusable form components
 */
const FormComponentDemo: React.FC = () => {
    const [formData, setFormData] = useState<FormData>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        notes: '',
        birthDate: '',
        preferredMethodOfContact: MethodOfContactTypes.Phone,
    });

    const [isEditMode, setIsEditMode] = useState(true);
    const [errors, setErrors] = useState<FormErrors>({});
    const [formValid, setFormValid] = useState(true);

    // Refs to track validation state of individual fields
    const firstNameValid = useRef(true);
    const lastNameValid = useRef(true);
    const emailValid = useRef(true);
    const phoneValid = useRef(true);

    const handleInputChange = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.currentTarget;
        setFormData({
            ...formData,
            [name]: value,
        });

        // Clear error when field is changed
        if (errors[name as keyof FormErrors]) {
            setErrors({
                ...errors,
                [name]: undefined,
            });
        }
    };

    const handleMethodOfContactChange = (method: MethodOfContactTypes) => {
        setFormData({
            ...formData,
            preferredMethodOfContact: method,
        });
    };

    const handleFieldValidation = (field: keyof FormErrors, isValid: boolean) => {
        switch (field) {
            case 'firstName':
                firstNameValid.current = isValid;
                break;
            case 'lastName':
                lastNameValid.current = isValid;
                break;
            case 'email':
                emailValid.current = isValid;
                break;
            case 'phone':
                phoneValid.current = isValid;
                break;
        }

        updateFormValidity();
    };

    const updateFormValidity = () => {
        const isFormValid = firstNameValid.current && lastNameValid.current && emailValid.current && phoneValid.current;
        setFormValid(isFormValid);
    };

    const validateEmail = (email: string): boolean => {
        return email === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const validatePhone = (phone: string): boolean => {
        return phone === '' || /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(phone);
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        let valid = true;

        // Validate first name (required)
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First Name is required';
            valid = false;
        }

        // Validate last name (required)
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last Name is required';
            valid = false;
        }

        // Validate email (optional with format validation)
        if (formData.email && !validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email';
            valid = false;
        }

        // Validate phone (optional with format validation)
        if (formData.phone && !validatePhone(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number';
            valid = false;
        }

        setErrors(newErrors);
        setFormValid(valid);
        return valid;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            alert('Form submitted successfully!');
            // Here you would typically send the data to an API
        }
    };

    const toggleEditMode = () => {
        setIsEditMode(!isEditMode);
    };

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="mb-6 flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-800">Form Components Demo</h1>
                <div className="flex space-x-3">
                    <button
                        onClick={toggleEditMode}
                        className={`${isEditMode ? 'bg-green-500' : 'bg-blue-500'} text-white px-4 py-2 rounded`}
                    >
                        {isEditMode ? 'View Mode' : 'Edit Mode'}
                    </button>

                    {isEditMode && (
                        <button
                            onClick={handleSubmit}
                            className={`${
                                formValid ? 'bg-purple-500 hover:bg-purple-600' : 'bg-purple-300 cursor-not-allowed'
                            } text-white px-4 py-2 rounded transition-colors`}
                            disabled={!formValid}
                        >
                            Submit Form
                        </button>
                    )}
                </div>
            </div>

            {isEditMode && Object.keys(errors).length > 0 && (
                <div className="bg-red-50 text-red-700 p-3 mb-4 rounded-md flex items-center">
                    <AlertCircle size={18} className="mr-2" />
                    <span>Please fix the validation errors before submitting</span>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h2 className="text-xl font-semibold mb-4">Basic Form Fields with Validation</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormTextField
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        disabled={!isEditMode}
                        required
                        error={errors.firstName}
                        onValidationChange={(isValid) => handleFieldValidation('firstName', isValid)}
                    />

                    <FormTextField
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        disabled={!isEditMode}
                        required
                        error={errors.lastName}
                        onValidationChange={(isValid) => handleFieldValidation('lastName', isValid)}
                    />

                    <FormTextField
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={!isEditMode}
                        error={errors.email}
                        validateField={() =>
                            formData.email && !validateEmail(formData.email) ? 'Please enter a valid email' : undefined
                        }
                        onValidationChange={(isValid) => handleFieldValidation('email', isValid)}
                    />

                    <FormField
                        label="Phone Number"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={!isEditMode}
                        placeholder="(123) 456-7890"
                        helperText="Include area code"
                        error={errors.phone}
                    />
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">Additional Form Fields</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                        label="Birth Date"
                        name="birthDate"
                        type="date"
                        value={formData.birthDate}
                        onChange={handleInputChange}
                        disabled={!isEditMode}
                    />

                    <FormField
                        label="Notes (Multiline)"
                        name="notes"
                        type="textarea"
                        value={formData.notes}
                        onChange={handleInputChange}
                        disabled={!isEditMode}
                        rows={4}
                        showEmptyState={!isEditMode && !formData.notes}
                        emptyStateText="No additional notes"
                    />

                    <div className="w-full my-2 px-4 flex flex-col">
                        <label className="block text-sm font-semibold text-gray-500 mb-1">
                            Preferred Contact Method
                        </label>
                        <div className="w-full md:w-1/2">
                            <MethodOfContact
                                selected={formData.preferredMethodOfContact}
                                updateMethodOfContact={handleMethodOfContactChange}
                                isDisabled={!isEditMode}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium mb-2">Form Data:</h3>
                <pre className="bg-gray-100 p-3 rounded text-sm">{JSON.stringify(formData, null, 2)}</pre>
            </div>

            {Object.keys(errors).length > 0 && (
                <div className="mt-4">
                    <div className="flex items-center text-red-500">
                        <AlertCircle size={16} className="mr-2" />
                        <span>Validation errors:</span>
                    </div>
                    <ul className="list-disc list-inside text-sm text-red-600 mt-2">
                        {errors.firstName && <li>{errors.firstName}</li>}
                        {errors.lastName && <li>{errors.lastName}</li>}
                        {errors.email && <li>{errors.email}</li>}
                        {errors.phone && <li>{errors.phone}</li>}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default FormComponentDemo;
